<template>
  <div class="widget w-market-calendar-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header fieldsConfigManager="simple" hideLinkMode printView :wid="wid">
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar>
      <v-form-group :label="i18n('labels.country')" label-cols="auto">
        <v-form-tags
          multicolor
          placeholder="Enter Country"
          :value="countries"
          :autocompleteMinLength="0"
          :autocompleteItems="countriesComplete"
          :add-only-from-autocomplete="true"
          @input="onUpdateCountries"
          @inputText="countryInput = $event"
        >
          <template slot="autocomplete-item" slot-scope="props">
            <div @click="props.performAdd(props.item)">
              {{ `${props.item.fullname}` }}
            </div>
          </template>
          <template slot="tag-left" slot-scope="props">
            <v-flag v-if="props.tag.id !== 'all'" :countryCode="props.tag.id" />
          </template>
        </v-form-tags>
      </v-form-group>

      <v-form-group :label="i18n('labels.indicators')" label-cols="auto">
        <v-form-tags
          multicolor
          placeholder="Enter Indicator"
          :value="indicators"
          :autocompleteMinLength="0"
          :autocompleteItems="indicatorsComplete"
          :add-only-from-autocomplete="true"
          @input="onUpdateIndicators"
          @inputText="indicatorInput = $event"
        />
      </v-form-group>

      <v-form-group :label="i18n('labels.from')" label-cols="auto">
        <v-form-datepicker
          value-as-date
          :value="fromDateValue"
          :locale="$i18n.locale"
          :labelHelp="$t('white-label.components.datepicker.labelHelp')"
          :labelPrevMonth="$t('white-label.components.datepicker.labelPrevMonth')"
          :labelCurrentMonth="$t('white-label.components.datepicker.labelCurrentMonth')"
          :labelNextMonth="$t('white-label.components.datepicker.labelNextMonth')"
          :labelPrevYear="$t('white-label.components.datepicker.labelPrevYear')"
          :labelCurrentYear="$t('white-label.components.datepicker.labelCurrentYear')"
          :labelNextYear="$t('white-label.components.datepicker.labelNextYear')"
          @input="onUpdateInputFrom"
        />
      </v-form-group>
      <v-form-group :label="i18n('labels.to')" label-cols="auto">
        <v-form-datepicker value-as-date :value="toDateValue" :locale="$i18n.locale" @input="onUpdateInputTo" />
      </v-form-group>
    </c-widget-control-bar>

    <div class="widget-body">
      <c-loader v-if="isLoading" />
      <c-streaming-error :show="hasStreamingError" />
      <b-table-lite
        v-if="!hasError && items.length > 0"
        v-show="!isLoading"
        :fields="fields"
        :items="items"
        bordered
        hover
        small
        sticky-header
        striped
      >
        <template v-for="field in fields" #[`head(${field.key})`]>
          {{ i18n(field.key + '.short', 'globalLabels.fids') }}
        </template>

        <template #cell(q.VALUE_DT1)="data">
          {{ getFormattedDateTime(data.value, data.item['q.VALUE_TS1']) }}
        </template>
        <template #cell(q._COUNTRY)="data">
          <v-flag :countryCode="data.value.toLowerCase()" />
        </template>
        <template #cell(q._DSPLY_NAME)="data">
          <span v-b-tooltip.hover.right :title="data.item['ecid.DESCRIPTION']">
            <i class="fas fa-question-circle fa-sm mr-1 indicator-desc-icon" aria-hidden="true" />
          </span>
          {{ data.value }}
        </template>
        <template #cell(q.GN_TXT24_1)="data">
          <v-fidlet
            v-if="data.value.trim() !== ''"
            :data="data.value || ''"
            type="float"
            :class="getCalcCellClasses(data.value, data.item['q.CTBTR_1'])"
          />
          <span v-else>{{ data.value }}</span>
        </template>

        <template #cell(q.CTBTR_1)="data">
          <v-fidlet v-if="data.value.trim() !== ''" :data="data.value || ''" type="float" />
          <span v-else>{{ data.value }}</span>
        </template>
        <template #cell(q.GV4_TEXT)="data">
          <v-fidlet v-if="data.value.trim() !== ''" :data="data.value || ''" type="float" />
          <span v-else>{{ data.value }}</span>
        </template>
        <template #cell(q.GN_TXT16_2)="data">
          <v-fidlet v-if="data.value.trim() !== ''" :data="data.value || ''" type="float" />
          <span v-else>{{ data.value }}</span>
        </template>

        <template #cell(q.OFFC_CODE2)="data">
          <v-fidlet
            v-if="data.value.trim() !== ''"
            :data="data.value || ''"
            type="float"
            :class="getCalcCellClasses(data.value, data.item['q.CTBTR_1'])"
          />
          <span v-else>{{ data.value }}</span>
        </template>
        <template #cell(q.GN_TXT16_4)="data">
          <v-fidlet
            v-if="data.value.trim() !== ''"
            :data="data.value || ''"
            type="float"
            :class="getCalcCellClasses(data.value, data.item['q.OFFC_CODE2'])"
          />
          <span v-else>{{ data.value }}</span>
        </template>
      </b-table-lite>

      <div v-if="hasError" class="text-center p-3">{{ i18n('labels.error') }}</div>
      <div v-if="!hasError && items.length === 0" class="text-center p-3">
        {{ i18n('labels.noData') }}
      </div>
    </div>
  </div>
</template>

<script>
import { API } from '@state/api';
import CLoader from '@custom/CLoader';
import CStreamingError from '@custom/CStreamingError';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import moment from 'moment';
import streamableFidsFilter from '@/utils/streamableFids';
import widgetCommons from '@mixins/widgetCommons';

import { toTimezoneDate } from '@/utils/date';

export default {
  name: 'w-market-calendar-widget',
  mixins: [widgetCommons],
  components: {
    CLoader,
    CStreamingError,
    CWidgetHeader,
    CWidgetHeaderButton,
    CWidgetControlBar
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      hasError: false,
      hasStreamingError: false,
      fids: [
        'q.VALUE_TS1',
        'q.VALUE_DT1',
        'q._COUNTRY',
        'q._ECI_CODE',
        'q.RIC',
        'q._DSPLY_NAME',
        'q.GV3_TEXT',
        'q.CTBTR_1LL',
        'q._ECI_REF_PRD',
        'q.GN_TXT16_4',
        'q.OFFC_CODE2',
        'q.GN_TXT24_1',
        'q.CTBTR_1',
        'q.GV4_TEXT',
        'q.GN_TXT16_2',
        'ecid.DESCRIPTION' // -> not streamable
      ],
      countryInput: '',
      allCountries: [],
      indicatorInput: '',
      dateFormat: 'DD-MM-YYYY',
      columnDefs: {
        'q.VALUE_DT1': {},
        'q._COUNTRY': {
          class: 'text-center'
        },
        'q._DSPLY_NAME': {
          tdClass: 'table-cell-indicator'
        },
        'q.GV3_TEXT': {
          tdClass: 'text-center'
        },
        'q.CTBTR_1LL': {
          tdClass: 'text-center'
        },
        'q.GN_TXT24_1': {
          tdClass: 'text-center'
        },
        'q.CTBTR_1': {
          tdClass: 'text-center'
        },
        'q.GV4_TEXT': {
          tdClass: 'text-center table-cell-reuters-high'
        },
        'q.GN_TXT16_2': {
          tdClass: 'text-center table-cell-reuters-low'
        },
        'q.OFFC_CODE2': {
          class: 'font-weight-bold',
          tdClass: 'text-center'
        },
        'q.GN_TXT16_4': {
          class: 'font-weight-bold',
          tdClass: 'text-center'
        }
      },
      items: [],
      subscriptionId: null
    };
  },
  computed: {
    locale() {
      return this.$store.state.applicationSettings.general.settings.applicationLanguage;
    },
    fields() {
      const fieldsConfig = this.$store.state.fieldsConfig.widgets.marketCalendar;
      return fieldsConfig.requiredFids
        .concat((fieldsConfig.templates.find(template => template.custom) || { fids: [] }).fids)
        .filter(fid => !fieldsConfig.invisibleFids.includes(fid))
        .map(fid => {
          return {
            key: fid,
            ...this.columnDefs[fid]
          };
        });
    },
    countriesValue() {
      return this.$store.state.widget[this.wid].countries;
    },
    countries() {
      if (!this.allCountries.length) return [];
      return this.countriesValue.map(country => this.allCountries.find(c => c.id === country));
    },
    countriesComplete() {
      return this.allCountries.filter(c => {
        return c.fullname.toLowerCase().indexOf(this.countryInput.toLowerCase()) !== -1;
      });
    },
    indicatorsValue() {
      return this.$store.state.widget[this.wid].indicators;
    },
    indicators() {
      return this.indicatorsValue.map(indicator => this.allIndicators.find(i => i.id === indicator));
    },
    allIndicators() {
      return [
        {
          text: this.i18n('indicators.all'),
          id: 'all'
        },
        {
          text: this.i18n('indicators.averageEarnings'),
          id: 'AVG'
        },
        {
          text: this.i18n('indicators.buildingPermits'),
          id: 'BPE'
        },
        {
          text: this.i18n('indicators.businessConditionsExpectations'),
          id: 'BUSC'
        },
        {
          text: this.i18n('indicators.businessOrConsumerConfidenceIndicator'),
          id: 'CONC'
        },
        {
          text: this.i18n('indicators.capacityUtilizationRate'),
          id: 'CAPU'
        },
        {
          text: this.i18n('indicators.changesInInventory'),
          id: 'INV'
        },
        {
          text: this.i18n('indicators.compositeLeadingIndicator'),
          id: 'LEAD'
        },
        {
          text: this.i18n('indicators.consumerCredit'),
          id: 'CRED'
        },
        {
          text: this.i18n('indicators.consumerPriceIndex'),
          id: 'CPI'
        },
        {
          text: this.i18n('indicators.consumerPriceIndexHarmonised'),
          id: 'HCPI'
        },
        {
          text: this.i18n('indicators.costOfLivingIndex'),
          id: 'COL'
        },
        {
          text: this.i18n('indicators.currentAccount'),
          id: 'CURA'
        },
        {
          text: this.i18n('indicators.export'),
          id: 'EXP'
        },
        {
          text: this.i18n('indicators.foreignExchangeReserve'),
          id: 'FXR'
        },
        {
          text: this.i18n('indicators.governmentBudgetBalance'),
          id: 'BUD'
        },
        {
          text: this.i18n('indicators.governmentDebt'),
          id: 'DEBT'
        },
        {
          text: this.i18n('indicators.grossDomesticProduct'),
          id: 'GDP'
        },
        {
          text: this.i18n('indicators.grossDomesticProductQQ'),
          id: 'GDPQ'
        },
        {
          text: this.i18n('indicators.grossDomesticProductYY'),
          id: 'GDPY'
        },
        {
          text: this.i18n('indicators.grossPersonalConsumption'),
          id: 'GPC'
        },
        {
          text: this.i18n('indicators.housingAndConstructionMarketIndicator'),
          id: 'BLD'
        },
        {
          text: this.i18n('indicators.import'),
          id: 'IMP'
        },
        {
          text: this.i18n('indicators.industrialProduction'),
          id: 'IP'
        },
        {
          text: this.i18n('indicators.interbankRate'),
          id: 'INTR'
        },
        {
          text: this.i18n('indicators.moneySupply'),
          id: 'MON'
        },
        {
          text: this.i18n('indicators.productionPriceIndex'),
          id: 'PPI'
        },
        {
          text: this.i18n('indicators.purchasingManagersIndex'),
          id: 'PMI'
        },
        {
          text: this.i18n('indicators.retailSales'),
          id: 'RSL'
        },
        {
          text: this.i18n('indicators.tradeBalance'),
          id: 'TBAL'
        },
        {
          text: this.i18n('indicators.unemploymentRate'),
          id: 'UNR'
        },
        {
          text: this.i18n('indicators.wholesalePriceIndex'),
          id: 'WPI'
        }
      ];
    },
    indicatorsComplete() {
      return this.allIndicators.filter(i => {
        return i.text.toLowerCase().indexOf(this.indicatorInput.toLowerCase()) !== -1;
      });
    },
    fromDateValue() {
      return this.$store.state.widget[this.wid].fromDate || toTimezoneDate(this.timezone);
    },
    toDateValue() {
      return this.$store.state.widget[this.wid].toDate || toTimezoneDate(this.timezone);
    },
    datesHash() {
      return [moment(this.fromDateValue).format('YYMMDD'), moment(this.toDateValue).format('YYMMDD')].join();
    },
    timezone() {
      return this.$store.state.applicationSettings.general.settings.timezone;
    }
  },
  watch: {
    datesHash() {
      this.fetch();
    }
  },
  methods: {
    onUpdateCountries(newTags) {
      if (newTags.length > 1 && newTags[0].id === 'all') {
        newTags = newTags.slice(1, newTags.length);
      } else if (newTags.length === 0 || (newTags.length > 0 && newTags[newTags.length - 1].id === 'all')) {
        newTags = [
          {
            text: this.i18n('all', 'countries'),
            id: 'all'
          }
        ];
      }

      const countries = newTags.map(tags => tags.id);
      this.$store.commit(`widget/${this.wid}/COUNTRIES_CHANGED`, countries);

      this.fetch();
    },
    onUpdateIndicators(newTags) {
      if (newTags.length > 1 && newTags[0].id === 'all') {
        newTags = newTags.slice(1, newTags.length);
      } else if (newTags.length === 0 || (newTags.length > 0 && newTags[newTags.length - 1].id === 'all')) {
        newTags = [
          {
            text: this.i18n('indicators.all'),
            id: 'all'
          }
        ];
      }

      const indicators = newTags.map(tags => tags.id);
      this.$store.commit(`widget/${this.wid}/INDICATORS_CHANGED`, indicators);
      this.fetch();
    },
    onUpdateInputFrom(fromDate) {
      this.$store.commit(`widget/${this.wid}/FROMDATE_CHANGED`, fromDate);
    },
    onUpdateInputTo(toDate) {
      this.$store.commit(`widget/${this.wid}/TODATE_CHANGED`, toDate);
    },
    getCalcCellClasses(fidValue, compareFidValue) {
      if (fidValue - compareFidValue > 0) {
        return 'positive';
      } else if (fidValue - compareFidValue < 0) {
        return 'negative';
      }
    },
    getFormattedDateTime(date, time) {
      const timeParts = time.split(':');
      let formattedTime = '[no time]';
      let formattedDate = moment(toTimezoneDate(this.timezone, date)).format('DD MMM YYYY');

      if (time.replace(/[\s:]+/g, '') !== '') {
        let d = new Date(date);
        d.setHours(timeParts[0], timeParts[1]);
        formattedTime = this.$f(d, 'time');
      }

      return `${formattedDate.toUpperCase()} ${formattedTime}`;
    },
    fetch() {
      this.hasError = false;
      this.hasStreamingError = false;
      this.isLoading = true;
      this.eventRics = [];
      this.items = [];

      if (this.countriesValue.length > 0 && this.indicatorsValue.length > 0) {
        // First unsubscribe from existing subscription (if any)
        this.streamingUnsubscribe();
        this.getCalendarEvents()
          .then(response => {
            this.eventRics = response.map(event => event['q.RIC']);
            this.streamingSubscribe(this.eventRics, this.fids);
          })
          .catch(e => {
            console.log(e);
            this.isLoading = false;
            this.hasError = true;
          });
      } else {
        this.streamingUnsubscribe();
        this.isLoading = false;
        this.items = [];
      }
    },
    getFilter(data, fid) {
      if (data[0] === 'all') {
        return '';
      }

      if (data.length === 1) {
        return '%26FID(' + fid + ")='" + data[0] + "'";
      } else if (data.length > 1) {
        let result = '';

        for (let i = 0; i < data.length; i++) {
          if (i === 0) {
            result += '%26(FID(' + fid + ")='" + data[i] + "'";
          } else if (i === data.length - 1) {
            result += '%7CFID(' + fid + ")='" + data[i] + "')";
          } else {
            result += '%7CFID(' + fid + ")='" + data[i] + "'";
          }
        }

        return result;
      }
    },
    getCalendarEvents() {
      const from = moment(this.fromDateValue)
        .format('DD MMM YYYY')
        .toUpperCase();
      const to = moment(this.toDateValue)
        .format('DD MMM YYYY')
        .toUpperCase();

      const dateFilter = "FID(VALUE_DT1)>='" + from + "'%26FID(VALUE_DT1)<='" + to + "'";
      const countryFilter = this.getFilter(this.countriesValue, '_COUNTRY');
      const indicatorFilter = this.getFilter(this.indicatorsValue, '_ECI_CODE');

      return API.Rest.get(
        '/economy/calendar?fids=' +
          this.fids +
          '&filter=' +
          dateFilter +
          countryFilter +
          indicatorFilter +
          '&sortFids=VALUE_DT1%2CVALUE_TS1&sortDirs=A%2CA'
      ).then(response => {
        if (response.data.data.length === 0) {
          this.items = [];
          throw new Error('NO_EVENTS_RECEIVED');
        }

        this.items = response.data.data;
        this.isLoading = false;
        return response.data.data;
      });
    },
    streamingConnect() {
      return new Promise((resolve, reject) => {
        if (!API.Streaming.isConnected) {
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(resolve, reject);
        } else {
          resolve();
        }
      });
    },
    streamingUnsubscribe() {
      if (this.subscriptionId !== null) {
        API.Streaming.Quote.unsubscribe(this.subscriptionId);
      }
    },
    streamingSubscribe(eventRics, fids) {
      this.subscriptionId = API.Streaming.Quote.subscribe(
        {
          rics: eventRics,
          fids: streamableFidsFilter(fids)
        },
        // Subscribe Callback
        this.handleSubscription,
        // Snapshot Callback
        this.handleSnapshot,
        // Update Callback
        this.handleUpdate
      );
    },
    handleSubscription(_response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming subscription error', error);
      } else {
        this.hasStreamingError = false;
      }
    },
    handleSnapshot(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming snapshot error', error);
        return;
      }

      this.hasStreamingError = false;

      try {
        response = response.payload[0];
        const item = this.items.find(item => item['q.RIC'] === response.ric);
        Object.assign(item, response.data);
      } catch (e) {
        console.error(e, response);
      }
    },
    handleUpdate(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming update error', error);
        return;
      }

      this.hasStreamingError = false;

      try {
        response = response.payload[0];
        const item = this.items.find(item => item['q.RIC'] === response.ric);
        Object.assign(item, response.data);
      } catch (e) {
        console.error(e, response);
      }
    },
    getCountries() {
      return API.Rest.get('/text/captiondomains?language=en&domains=ISO3166A2&captionType=SHORT');
    },
    processCountriesData(data) {
      let allCountriesProcessed = Object.keys(data).map(country => {
        return {
          text: data[country],
          fullname: `${data[country]} (${country})`,
          id: country
        };
      });
      allCountriesProcessed.unshift({
        text: this.i18n('all', 'countries'),
        fullname: this.i18n('all', 'countries'),
        id: 'all'
      });

      return allCountriesProcessed;
    }
  },
  mounted() {
    this.getCountries().then(data => {
      this.allCountries = this.processCountriesData(data.data.data[0].ISO3166A2.SHORT);
      this.streamingConnect().then(this.fetch);
    });
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/market-calendar-widget';
</style>
